import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import IconButton from "@react/shared/ui/IconButton"

// Compact numbered pagination — prev / next + a window of page numbers around
// the current page with ellipsis for the gaps.
const buildPageWindow = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1)

  const window = new Set([1, total, current - 1, current, current + 1])
  const sorted = [...window].filter(page => page >= 1 && page <= total).sort((left, right) => left - right)

  const withGaps = []
  sorted.forEach((page, index) => {
    if (index > 0 && page - sorted[index - 1] > 1) withGaps.push("ellipsis")
    withGaps.push(page)
  })
  return withGaps
}

const Pagination = ({ pagination, onPageChange }) => {
  if (!pagination || pagination.total_count === 0) {
    return (
      <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 text-sm text-slate-500">
        <div>{I18n.t("quotes.project_quote_templates.pagination.no_results")}</div>
      </div>
    )
  }

  const { current_page, total_pages, total_count, per_page } = pagination
  const rangeStart = (current_page - 1) * per_page + 1
  const rangeEnd   = Math.min(current_page * per_page, total_count)
  const pageWindow = buildPageWindow(current_page, total_pages)

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 text-sm text-slate-500">
      <div>
        {I18n.t("quotes.project_quote_templates.pagination.showing_prefix")}{" "}
        <span className="font-medium text-slate-900">{rangeStart}-{rangeEnd}</span>
        {" "}{I18n.t("quotes.project_quote_templates.pagination.showing_of")}{" "}
        <span className="font-medium text-slate-900">{total_count}</span>
      </div>

      {total_pages > 1 && (
        <div className="flex items-center gap-1">
          <IconButton
            size     = "md"
            disabled = {current_page <= 1}
            onClick  = {() => onPageChange(current_page - 1)}
          >
            <FontAwesomeIcon icon="chevron-left" className="text-[10px]" />
          </IconButton>

          {pageWindow.map((item, index) =>
            item === "ellipsis" ? (
              <span key={`ellipsis-${index}`} className="text-slate-400 px-1">…</span>
            ) : item === current_page ? (
              <button
                key       = {item}
                type      = "button"
                className = "h-8 w-8 rounded-md bg-slate-900 text-white text-sm font-medium"
              >
                {item}
              </button>
            ) : (
              <IconButton
                key     = {item}
                size    = "md"
                onClick = {() => onPageChange(item)}
                className = "text-sm font-medium"
              >
                {item}
              </IconButton>
            )
          )}

          <IconButton
            size     = "md"
            disabled = {current_page >= total_pages}
            onClick  = {() => onPageChange(current_page + 1)}
          >
            <FontAwesomeIcon icon="chevron-right" className="text-[10px]" />
          </IconButton>
        </div>
      )}
    </div>
  )
}

export default Pagination
